import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Plus, Trash2, Edit2, ArrowLeft, FileText, Settings as SettingsIcon, Mail, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'application', label: 'Προθεσμίες Αιτήσεων', icon: FileText, color: 'emerald' },
  { id: 'implementation', label: 'Προθεσμίες Υλοποίησης', icon: SettingsIcon, color: 'blue' },
];

const Reminders = ({ user }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('application');
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ programs: [], customers: [] });

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // reminder being edited
  const [form, setForm] = useState(blankForm('application'));

  const [confirmDelete, setConfirmDelete] = useState(null);

  function blankForm(category) {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return {
      category,
      program_name: '',
      client_name: '',
      message: '',
      reminder_date: `${yyyy}-${mm}-${dd}`,
      reminder_time: '09:00',
      delete_after_days: 7,
      program_id: null,
      client_id: null,
    };
  }

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reminders`, {
        params: { user_id: user.user_id, category: tab },
      });
      setReminders(res.data.reminders || []);
    } catch (e) {
      toast.error('Αποτυχία φόρτωσης υπενθυμίσεων');
    } finally {
      setLoading(false);
    }
  }, [tab, user.user_id]);

  const loadOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reminders/options`, {
        params: { user_id: user.user_id },
      });
      setOptions(res.data || { programs: [], customers: [] });
    } catch (e) {
      // non-fatal
    }
  }, [user.user_id]);

  useEffect(() => { loadReminders(); }, [loadReminders]);
  useEffect(() => { loadOptions(); }, [loadOptions]);

  const openNew = () => {
    setEditing(null);
    setForm(blankForm(tab));
    setShowDialog(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      category: r.category,
      program_name: r.program_name || '',
      client_name: r.client_name || '',
      message: r.message || '',
      reminder_date: r.reminder_date,
      reminder_time: r.reminder_time || '09:00',
      delete_after_days: r.delete_after_days || 7,
      program_id: r.program_id,
      client_id: r.client_id,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.program_name.trim()) { toast.error('Συμπληρώστε όνομα προγράμματος'); return; }
    if (!form.client_name.trim()) { toast.error('Συμπληρώστε όνομα πελάτη'); return; }
    if (!form.message.trim()) { toast.error('Συμπληρώστε κείμενο υπενθύμισης'); return; }
    if (!form.reminder_date) { toast.error('Συμπληρώστε ημερομηνία'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/reminders/${editing.reminder_id}?user_id=${user.user_id}`, form);
        toast.success('Η υπενθύμιση ενημερώθηκε');
      } else {
        await axios.post(`${API}/reminders?user_id=${user.user_id}`, form);
        toast.success('Η υπενθύμιση δημιουργήθηκε');
      }
      setShowDialog(false);
      loadReminders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/reminders/${confirmDelete.reminder_id}?user_id=${user.user_id}`);
      toast.success('Διαγράφηκε');
      setConfirmDelete(null);
      loadReminders();
    } catch (e) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const formatDate = (d, t) => {
    try {
      const [y, m, dd] = (d || '').split('-');
      return `${dd}/${m}/${y} ${t || ''}`;
    } catch { return `${d} ${t}`; }
  };

  const statusBadge = (r) => {
    if (r.status === 'sent') return <Badge className="bg-slate-200 text-slate-700 border-slate-300">Στάλθηκε</Badge>;
    if (r.status === 'failed') return <Badge className="bg-rose-100 text-rose-700 border-rose-300">Αποτυχία</Badge>;
    if (r.is_overdue) return <Badge className="bg-rose-500 text-white animate-pulse" data-testid="reminder-overdue-badge">Εκπρόθεσμη</Badge>;
    if (r.is_today) return <Badge className="bg-amber-500 text-white" data-testid="reminder-today-badge">Σήμερα</Badge>;
    if (r.is_tomorrow) return <Badge className="bg-amber-100 text-amber-800 border-amber-300">Αύριο</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Προγραμματισμένη</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/select-module')} data-testid="reminders-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Πίσω
          </Button>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Clock className="h-6 w-6 text-amber-500" /> Προθεσμίες & Υπενθυμίσεις
            </h1>
            <p className="text-sm text-slate-500 hidden md:block">Θα λαμβάνετε email την επιλεγμένη ημερομηνία και ώρα.</p>
          </div>
          <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600" data-testid="reminders-new-btn">
            <Plus className="h-4 w-4 mr-1" /> Νέα Υπενθύμιση
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 mb-6">
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.id} value={c.id} data-testid={`reminders-tab-${c.id}`}>
                <c.icon className="h-4 w-4 mr-2" />{c.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((c) => (
            <TabsContent key={c.id} value={c.id} className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-slate-400">Φόρτωση...</div>
              ) : reminders.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
                  <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Δεν υπάρχουν υπενθυμίσεις σε αυτή την κατηγορία.</p>
                  <Button onClick={openNew} variant="outline" className="mt-4" data-testid="reminders-empty-new-btn">
                    <Plus className="h-4 w-4 mr-1" /> Δημιουργία πρώτης υπενθύμισης
                  </Button>
                </div>
              ) : (
                reminders.map((r) => (
                  <div key={r.reminder_id}
                    className={`bg-white rounded-xl border p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 ${r.is_overdue ? 'border-rose-300 bg-rose-50/50' : r.is_today ? 'border-amber-300' : 'border-slate-200'}`}
                    data-testid={`reminder-card-${r.reminder_id}`}>
                    <div className="flex-shrink-0 w-14 h-14 bg-slate-50 rounded-lg flex flex-col items-center justify-center border border-slate-200">
                      <div className="text-xs text-slate-500 font-medium">{(r.reminder_date || '').split('-')[2]}</div>
                      <div className="text-[10px] text-slate-400">{r.reminder_time}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900 truncate">{r.program_name}</span>
                        {statusBadge(r)}
                      </div>
                      <div className="text-sm text-slate-600 mb-1">
                        <span className="text-slate-400">Πελάτης:</span> {r.client_name}
                      </div>
                      <div className="text-sm text-slate-700 line-clamp-2">{r.message}</div>
                      <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />{formatDate(r.reminder_date, r.reminder_time)}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {r.status !== 'sent' && (
                        <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`reminder-edit-${r.reminder_id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => setConfirmDelete(r)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`reminder-delete-${r.reminder_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              {editing ? 'Επεξεργασία Υπενθύμισης' : 'Νέα Υπενθύμιση'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Κατηγορία</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c.id}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition ${form.category === c.id ? (c.id === 'application' ? 'bg-emerald-50 border-emerald-400 text-emerald-800' : 'bg-blue-50 border-blue-400 text-blue-800') : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    onClick={() => setForm({ ...form, category: c.id })}
                    data-testid={`reminder-form-cat-${c.id}`}>
                    <c.icon className="h-4 w-4 inline mr-1" />{c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Όνομα Προγράμματος *</Label>
              <Input list="programs-list" value={form.program_name}
                onChange={(e) => setForm({ ...form, program_name: e.target.value, program_id: null })}
                placeholder="π.χ. Ψηφιακός Μετασχηματισμός ΜμΕ"
                data-testid="reminder-form-program" />
              <datalist id="programs-list">
                {(options.programs || []).map((p) => (
                  <option key={p.program_id || p.name} value={p.name} />
                ))}
              </datalist>
            </div>

            <div>
              <Label>Όνομα Πελάτη *</Label>
              <Input list="clients-list" value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value, client_id: null })}
                placeholder="π.χ. Παπαδόπουλος Α.Ε."
                data-testid="reminder-form-client" />
              <datalist id="clients-list">
                {(options.customers || []).map((c) => (
                  <option key={c.customer_id} value={c.name} />
                ))}
              </datalist>
            </div>

            <div>
              <Label>Κείμενο Ειδοποίησης *</Label>
              <Textarea rows={3} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Τι να θυμηθώ αυτή τη μέρα;"
                data-testid="reminder-form-message" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ημερομηνία *</Label>
                <Input type="date" value={form.reminder_date}
                  onChange={(e) => setForm({ ...form, reminder_date: e.target.value })}
                  data-testid="reminder-form-date" />
              </div>
              <div>
                <Label>Ώρα</Label>
                <Input type="time" value={form.reminder_time}
                  onChange={(e) => setForm({ ...form, reminder_time: e.target.value })}
                  data-testid="reminder-form-time" />
              </div>
            </div>

            <div>
              <Label>Αυτόματη διαγραφή μετά από (ημέρες)</Label>
              <Input type="number" min="1" max="365" value={form.delete_after_days}
                onChange={(e) => setForm({ ...form, delete_after_days: parseInt(e.target.value) || 7 })}
                data-testid="reminder-form-ttl" />
              <p className="text-xs text-slate-400 mt-1">Αφού σταλεί το email, η υπενθύμιση θα διαγραφεί αυτόματα μετά από τόσες ημέρες.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="reminder-form-cancel">Ακύρωση</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600" data-testid="reminder-form-save">
              <Mail className="h-4 w-4 mr-1" />{editing ? 'Αποθήκευση' : 'Δημιουργία'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Διαγραφή Υπενθύμισης;</AlertDialogTitle>
            <AlertDialogDescription>
              Θα διαγραφεί οριστικά η υπενθύμιση «{confirmDelete?.program_name}». Αυτή η ενέργεια δεν αναιρείται.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reminder-delete-cancel">Ακύρωση</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="reminder-delete-confirm">
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reminders;
